export const normalizeDigits = (value) => String(value ?? '').replace(/[۰-۹٠-٩]/g, (digit) => {
  const code = digit.charCodeAt(0);
  return String(code >= 0x06f0 ? code - 0x06f0 : code - 0x0660);
});

export const getPersianLabel = (dictionary, value, namespace) => {
  const key = value == null ? '' : String(value);
  const label = key ? dictionary?.[key] : undefined;

  if (typeof label === 'string' && label) return label;

  if (key && import.meta.env.DEV) {
    console.warn(`[i18n] Missing Persian translation: ${namespace}.${key || '(empty)'}`);
  }

  return 'نامشخص';
};

export const persianDisplayText = (value, fallback = '—') => {
  if (value == null || value === '') return fallback;
  const text = String(value);
  return /\p{Script=Latin}/u.test(text) ? fallback : text;
};

const ERROR_MESSAGES = {
  CUSTOMER_LOCATION_REQUIRED: 'برای ادامهٔ خرید، نشانی، کد پستی، شهر و موقعیت خود را در پروفایل ثبت کنید.',
  CAPTCHA_REQUIRED: 'برای ادامه، تأیید امنیتی را تکمیل کنید.',
  CAPTCHA_FAILED: 'تأیید امنیتی ناموفق بود. لطفاً دوباره تلاش کنید.',
  CAPTCHA_UNAVAILABLE: 'سرویس تأیید امنیتی موقتاً در دسترس نیست.',
  OTP_BLOCKED: 'به‌دلیل تلاش‌های ناموفق، دریافت کد موقتاً مسدود شده است.',
  OTP_COOLDOWN: 'برای دریافت دوبارهٔ کد، کمی صبر کنید.',
  OTP_DAILY_CAP: 'سقف روزانهٔ درخواست کد تکمیل شده است.',
  OTP_VERIFY_DAILY_CAP: 'سقف روزانهٔ تلاش برای تأیید کد تکمیل شده است.',
  VALIDATION_ERROR: 'اطلاعات واردشده معتبر نیست.',
  RATE_LIMITED: 'تعداد درخواست‌ها بیش از حد مجاز است. کمی بعد دوباره تلاش کنید.',
  NETWORK_ERROR: 'ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی و دوباره تلاش کنید.',
  INTERNAL_SERVER_ERROR: 'خطای غیرمنتظره‌ای رخ داد. لطفاً بعداً دوباره تلاش کنید.',
  HTTP_400: 'درخواست نامعتبر است.',
  HTTP_401: 'برای ادامه، لطفاً وارد حساب کاربری خود شوید.',
  HTTP_403: 'شما مجاز به انجام این کار نیستید.',
  HTTP_404: 'مورد درخواستی پیدا نشد.',
  HTTP_409: 'این درخواست با وضعیت فعلی اطلاعات سازگار نیست.',
  HTTP_422: 'اطلاعات واردشده معتبر نیست.',
  HTTP_429: 'تعداد درخواست‌ها بیش از حد مجاز است. کمی بعد دوباره تلاش کنید.',
  HTTP_500: 'خطای غیرمنتظره‌ای رخ داد. لطفاً بعداً دوباره تلاش کنید.',
  HTTP_503: 'سرویس موقتاً در دسترس نیست. لطفاً کمی بعد دوباره تلاش کنید.',
  CART_EMPTY: 'سبد خرید شما خالی است.',
  ORDER_NOT_FOUND: 'سفارش پیدا نشد.',
  NOT_AUTHORIZED: 'شما مجاز به انجام این کار نیستید.',
  USER_NOT_FOUND: 'کاربر پیدا نشد.',
  VENDOR_NOT_VERIFIED: 'حساب فروشنده شما هنوز توسط مدیر تأیید نشده است.',
  MOBILE_EXISTS: 'این شمارهٔ همراه قبلاً در سامانه ثبت شده است.',
  NATIONAL_ID_EXISTS: 'این کد ملی قبلاً در سامانه ثبت شده است.',
  SHOP_NAME_TAKEN: 'این نام فروشگاه قبلاً ثبت شده است.',
  VENDOR_PHONE_TAKEN: 'این شمارهٔ تلفن ثابت قبلاً برای فروشگاه دیگری ثبت شده است.',
  SHABA_TAKEN: 'این شمارهٔ شبا قبلاً ثبت شده است.',
  COMPANY_EXISTS: 'شرکتی با این نام یا شناسهٔ ملی قبلاً ثبت شده است.',
  COMPANY_NAME_EN_TAKEN: 'این نام انگلیسی شرکت قبلاً ثبت شده است.',
};

const VALIDATION_MESSAGES = {
  'Field required': 'وارد کردن این مورد الزامی است.',
  'Input should be a valid string': 'مقدار باید متن معتبر باشد.',
  'Input should be a valid integer': 'مقدار باید عدد صحیح باشد.',
  'Input should be a valid number': 'مقدار باید عدد معتبر باشد.',
  'Input should be a valid UUID': 'شناسهٔ واردشده معتبر نیست.',
  'Input should be a valid email address': 'نشانی ایمیل معتبر نیست.',
  'Extra inputs are not permitted': 'این مورد پشتیبانی نمی‌شود.',
  'String should match pattern': 'قالب مقدار واردشده معتبر نیست.',
};

const containsPersian = (text) => /\p{Script=Arabic}/u.test(text);
const genericError = 'انجام درخواست با مشکل روبه‌رو شد. لطفاً دوباره تلاش کنید.';

export const getPersianValidationMessage = (item) => {
  if (typeof item?.msg === 'string' && VALIDATION_MESSAGES[item.msg]) {
    return VALIDATION_MESSAGES[item.msg];
  }
  if (item?.type === 'missing') return 'وارد کردن این مورد الزامی است.';
  if (item?.type === 'string_too_short') return 'تعداد نویسه‌های واردشده کمتر از حد مجاز است.';
  if (item?.type === 'string_too_long') return 'تعداد نویسه‌های واردشده بیشتر از حد مجاز است.';
  if (item?.type === 'extra_forbidden') return 'این مورد پشتیبانی نمی‌شود.';
  return 'مقدار واردشده معتبر نیست.';
};

const safeFallback = (fallback) => (
  typeof fallback === 'string' && containsPersian(fallback)
    ? fallback
    : genericError
);

export const getPersianErrorMessage = (error, fallback = genericError) => {
  if (Array.isArray(error)) {
    return error.map(getPersianValidationMessage).join('، ') || safeFallback(fallback);
  }

  const detail = error?.detail;
  const code = String(
    error?.code
      ?? error?.error_code
      ?? (detail && typeof detail === 'object' ? detail.code : '')
      ?? '',
  ).toUpperCase();
  if (Array.isArray(detail)) {
    return detail.map(getPersianValidationMessage).join('، ') || safeFallback(fallback);
  }
  if (ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];

  const detailMessage = typeof detail === 'string'
    ? detail
    : detail && typeof detail === 'object'
      ? detail.message
      : undefined;
  const message = detailMessage ?? error?.message ?? error?.error;
  if (typeof message === 'string') {
    if (containsPersian(message) && !/\p{Script=Latin}/u.test(message)) return message;
    const key = message.trim().toLowerCase();
    const translated = {
      'not authenticated': ERROR_MESSAGES.HTTP_401,
      'user not found': 'کاربر پیدا نشد.',
      'order not found': 'سفارش پیدا نشد.',
      'transaction not found': 'تراکنش پیدا نشد.',
      'basket is empty': 'سبد خرید شما خالی است.',
      'not authorized': ERROR_MESSAGES.NOT_AUTHORIZED,
      'not authorized.': ERROR_MESSAGES.NOT_AUTHORIZED,
      'invalid or expired code': 'کد واردشده نامعتبر است یا اعتبار آن به پایان رسیده است.',
      'too many requests': ERROR_MESSAGES.RATE_LIMITED,
      'failed to fetch': ERROR_MESSAGES.NETWORK_ERROR,
      'networkerror when attempting to fetch resource.': ERROR_MESSAGES.NETWORK_ERROR,
    }[key];
    if (translated) return translated;
  }

  const status = Number(error?.status ?? error?.response?.status);
  return ERROR_MESSAGES[`HTTP_${status}`] ?? safeFallback(fallback);
};
